/**
 * Client-side upload → transcribe pipeline for the editor. Turns "Load video"
 * into the real flow: create project → signed direct-to-storage upload → confirm
 * → poll → load the REAL transcript (with a Devanagari font when the transcript
 * is Hindi, matching what the export does).
 */
import { validateVideoUpload } from './validateVideoUpload';
import { api, ApiClientError } from '../api/client';
import { useEditor } from '../state/editorStore';
import type { WordStyle } from '@captionflow/caption-engine';

export type StageSetter = (stage: string | null) => void;

interface TranscriptResponse {
  durationMs: number | null;
  sourceUrl: string | null;
  proxyUrl?: string | null;
  language: string | null;
  templateId?: string | null;
  aspectRatio?: import('../state/editorStore').AspectKey | null;
  sourceWidth?: number | null;
  sourceHeight?: number | null;
  words: {
    id: string;
    text: string;
    startMs: number;
    endMs: number;
    confidence?: number;
    emphasized: boolean;
    hidden: boolean;
    isFiller: boolean;
    colorOverride?: string;
    sizeScale?: number;
    bold?: boolean;
    depthBehind?: boolean;
    wordStyle?: WordStyle | null;
  }[];
  segments?: {
    id: string;
    wordIds: string[];
    startMs: number;
    endMs: number;
    templateId?: string | null;
  }[];
  styleOverrides?: Record<string, unknown>;
  placementOverride?: Record<string, unknown>;
  transitions?: { id: string; kind: 'fade' | 'flash' | 'zoomIn' | 'zoomOut' | 'slide'; startMs: number; durationMs: number }[];
  brolls?: import('../state/editorStore').Broll[];
  hooks?: import('../state/editorStore').HookMoment[];
  audio?: {
    originalVolume: number;
    originalMuted: boolean;
    music?: { name: string; url?: string; storageKey?: string; volume: number; loop: boolean; duck: boolean; startMs?: number; durationMs?: number } | null;
    sfx?: import('../state/editorStore').SfxClip[];
  } | null;
  initial?: import('../state/editorStore').InitialSnapshot | null;
}

export function loadTranscriptIntoEditor(tr: TranscriptResponse, videoUrl: string | null): void {
  const words = tr.words.map((w) => ({
    id: w.id,
    text: w.text,
    startMs: w.startMs,
    endMs: w.endMs,
    confidence: w.confidence,
    emphasized: w.emphasized,
    hidden: w.hidden,
    isFiller: w.isFiller,
    colorOverride: w.colorOverride,
    sizeScale: w.sizeScale,
    bold: w.bold,
    depthBehind: w.depthBehind,
    wordStyle: w.wordStyle ?? undefined,
  }));
  // Restore SAVED caption lines (their breaks + per-line template overrides)
  // when present, so AI-enhanced breaks and per-window styles survive a reload.
  // Fall back to auto-segmentation only when there are no saved segments.
  const segments = (tr.segments ?? [])
    .filter((s) => s.wordIds.length > 0)
    .map((s) => ({
      id: s.id,
      wordIds: s.wordIds,
      startMs: s.startMs,
      endMs: s.endMs,
      templateId: s.templateId ?? undefined,
    }));
  useEditor.getState().load({
    words,
    segments: segments.length ? segments : undefined,
    templateId: tr.templateId ?? undefined,
    durationMs: tr.durationMs ?? 0,
    videoUrl: videoUrl ?? tr.sourceUrl,
    transitions: tr.transitions ?? [],
    styleOverrides: (tr.styleOverrides as never) ?? {},
    placementOverride: (tr.placementOverride as never) ?? {},
    aspect: tr.aspectRatio ?? '9:16',
    sourceDims: tr.sourceWidth && tr.sourceHeight ? { w: tr.sourceWidth, h: tr.sourceHeight } : null,
  });
  // Keep the as-created snapshot for the kMagic "Reset to original" button.
  useEditor.setState({ initialSnapshot: tr.initial ?? null });
  // Restore saved B-roll (durable storageKey + a fresh presigned preview URL).
  useEditor.setState({ brolls: (tr.brolls ?? []).map((b) => ({ ...b })), selectedBrollId: null });
  // Restore saved hook moments / takeovers.
  useEditor.setState({ hooks: (tr.hooks ?? []).map((h) => ({ ...h })), selectedHookId: null });
  // Restore saved audio (original level/mute + external music, e.g. kMagic's pick).
  if (tr.audio) {
    useEditor.setState({
      audio: {
        originalVolume: tr.audio.originalVolume ?? 1,
        originalMuted: tr.audio.originalMuted ?? false,
        // API presigns the storageKey → url; keep the key so future saves persist it.
        music: tr.audio.music ? { ...tr.audio.music, url: tr.audio.music.url ?? '' } : null,
        sfx: (tr.audio.sfx ?? []).map((x) => ({ ...x, url: x.url ?? '' })),
      },
    });
  }
  // Devanagari/Hinglish fonts are chosen per-word by the layout engine, so no
  // global font override is needed here.
}

export class NeedsAuthError extends Error {}
export class NeedsProjectError extends Error {}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function buildCaptionsPayload() {
  const st = useEditor.getState();
  return {
    templateId: st.templateId,
    aspectRatio: st.aspect,
    styleOverrides: st.styleOverrides,
    placementOverride: st.placementOverride,
    transitions: st.transitions,
    // Only clips that finished uploading (have a storageKey) can be persisted.
    brolls: st.brolls
      .filter((b) => b.storageKey)
      .map((b) => ({
        id: b.id,
        kind: b.kind,
        name: b.name,
        storageKey: b.storageKey,
        startMs: b.startMs,
        durationMs: b.durationMs,
        mode: b.mode,
        x: b.x,
        y: b.y,
        scale: b.scale,
        fit: b.fit,
      })),
    hooks: st.hooks.map((h) => ({ ...h })),
    words: st.words.map((w) => ({
      id: w.id,
      text: w.text,
      startMs: w.startMs,
      endMs: w.endMs,
      emphasized: w.emphasized ?? false,
      hidden: w.hidden ?? false,
      isFiller: w.isFiller ?? false,
      colorOverride: w.colorOverride,
      sizeScale: w.sizeScale,
      bold: w.bold,
      depthBehind: w.depthBehind,
      wordStyle: w.wordStyle ?? undefined,
    })),
    segments: st.segments.map((s) => ({
      wordIds: s.wordIds,
      startMs: s.startMs,
      endMs: s.endMs,
      templateId: s.templateId ?? null,
    })),
    audio: {
      originalVolume: st.audio.originalVolume,
      originalMuted: st.audio.originalMuted,
      // Persist an uploaded track by its durable storageKey, or an external
      // (http) track by URL. A blob without a storageKey isn't persistable yet.
      music: (() => {
        const m = st.audio.music;
        if (!m) return null;
        const common = { name: m.name, volume: m.volume, loop: m.loop, duck: m.duck, startMs: m.startMs, durationMs: m.durationMs };
        if (m.storageKey) return { ...common, storageKey: m.storageKey };
        if (/^https?:/.test(m.url)) return { ...common, url: m.url };
        return null;
      })(),
      // Timed sound effects: persist those with a durable key or an external URL.
      sfx: (st.audio.sfx ?? [])
        .map((x) => {
          const common = { id: x.id, name: x.name, startMs: x.startMs, durationMs: x.durationMs, volume: x.volume };
          if (x.storageKey) return { ...common, storageKey: x.storageKey };
          if (/^https?:/.test(x.url)) return { ...common, url: x.url };
          return null;
        })
        .filter(Boolean),
    },
  };
}

/** Persist the current editor state (words, segments, template, style) to the project. */
export async function saveEdits(): Promise<void> {
  const projectId = useEditor.getState().projectId;
  if (!projectId) throw new NeedsProjectError();
  useEditor.setState({ saveState: 'saving' });
  await api.saveCaptions(projectId, buildCaptionsPayload());
  // Only clear the dirty flag if no edit landed while the request was in flight
  // (that edit would have flipped state back to 'dirty' and must still save).
  if (useEditor.getState().saveState === 'saving') useEditor.getState().markSaved();
}

/** Save edits, kick off a render, poll to completion, return a download URL. */
export async function saveAndExport(aspect: string, setStage: StageSetter): Promise<string> {
  const projectId = useEditor.getState().projectId;
  if (!projectId) throw new NeedsProjectError();

  setStage('Saving edits…');
  await api.saveCaptions(projectId, buildCaptionsPayload());
  useEditor.getState().markSaved();

  setStage('Rendering…');
  const { renderJobId } = await api.export(projectId, {
    aspectRatio: aspect,
    fit: 'fill',
    resolution: '1080p',
    fps: 30,
    watermark: useEditor.getState().watermark,
  });

  for (let i = 0; i < 400; i++) {
    const j = await api.getExport(projectId, renderJobId);
    if (j.status === 'completed') break;
    if (j.status === 'failed') throw new Error(j.error || 'Render failed');
    setStage(`Rendering… ${Math.round((j.progress || 0) * 100)}%`);
    await sleep(1500);
  }

  const dl = await api.getExportDownload(projectId, renderJobId);
  setStage(null);
  return dl.url;
}

export async function uploadAndTranscribe(file: File, setStage: StageSetter): Promise<string> {
  setStage('Checking video…');
  const durationMs = await validateVideoUpload(file);
  // Require a session - transcription runs server-side.
  try {
    await api.me();
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) throw new NeedsAuthError();
    throw err;
  }

  // Instant local preview while we process.
  const localUrl = URL.createObjectURL(file);
  useEditor.setState({ videoUrl: localUrl });

  setStage('Creating project…');
  const name = file.name.replace(/\.[^.]+$/, '') || 'Untitled';
  const project = await api.createProject(name, '9:16');
  useEditor.setState({ projectId: project.id });

  setStage('Uploading…');
  const contentType = file.type || 'video/mp4';
  const up = await api.requestUpload(project.id, file.name, contentType, file.size, durationMs);
  const put = await fetch(up.url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  setStage('Processing video…');
  await api.confirmUpload(project.id, up.uploadId);

  setStage('Transcribing…');
  for (let i = 0; i < 200; i++) {
    const p = await api.getProject(project.id);
    if (p.transcriptionStatus === 'completed') break;
    if (p.status === 'failed') throw new Error('Processing failed');
    await new Promise((r) => setTimeout(r, 1500));
  }

  const tr = (await api.getTranscript(project.id)) as TranscriptResponse;
  if (!tr.words.length) throw new Error('Transcription produced no words');
  loadTranscriptIntoEditor(tr, localUrl);
  setStage(null);
  // Update the URL so a refresh reloads this project.
  window.history.replaceState(null, '', `/editor/${project.id}`);
  return project.id;
}
